import { useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

export const slug = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w]+/g, "-").toLowerCase();
export const beneficiaire = (d) => d.replace(/\(part\)$/, "").replace(/\d{3,}/g, "").replace(/\s+/g, " ").replace(/[\s/#-]+$/, "").trim() || d;
export const cleGroupe = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s*\/\s*/g, "/").replace(/\s+/g, " ");

const LOT = "/transactions/commentaire-lot";
const COMPAGNIE = "Dépenses de la compagnie";
const TRIGGER = "h-7 w-full min-w-[9rem] gap-1 border-slate-200 bg-white px-2 text-[11px] text-emerald-700";
const nomCible = (c) => (c.imm ? (c.cat.valeur === "immeuble" ? `le revenu de ${c.imm}` : `${c.cat.libelle.replace(" (par immeuble)", "")} ${c.imm}`) : c.cat.libelle);

const annulerDeplacement = (ids, data, memoriser, onClasse, onAnnule) => async () => {
  try {
    if (memoriser && Object.keys(data.memo_avant).length) { await api.post(LOT, { ids: [ids[0]], champs: data.memo_avant, memoriser: true }); }
    for (const [id, champs] of Object.entries(data.avant)) { await api.post(LOT, { ids: [id], champs, memoriser: false }); }
    toast.success("Déplacement annulé");
    onAnnule(ids);
    onClasse();
  } catch (e) { toast.error(errMsg(e)); }
};

const Portee = ({ benef, n, envoyer, retour, testId }) => (
  <div className="flex flex-wrap items-center gap-1" data-testid={`${testId}-portee`}>
    <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => envoyer(false)} data-testid={`${testId}-seule`}>Cette opération</Button>
    <Button size="sm" variant="outline" className="h-7 max-w-[14rem] px-2 text-[11px]" onClick={() => envoyer(true)} title={`Toutes les opérations de « ${benef} », passées et futures`} data-testid={`${testId}-toutes`}>
      <span className="truncate">Tout « {benef} »{n > 1 ? ` (${n})` : ""}</span>
    </Button>
    <button type="button" onClick={retour} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Annuler" data-testid={`${testId}-retour`}><X className="h-3.5 w-3.5" /></button>
  </div>
);

// Déplace une opération du détail d'une catégorie vers une autre catégorie (et son immeuble si besoin)
export const DeplacerOperation = ({ t, memes, cl, actuelle, onDeplace, onAnnule, testId }) => {
  const [cible, setCible] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const benef = beneficiaire(t.description);
  const envoyer = async (memoriser) => {
    const ids = memoriser ? memes : [t.id];
    const champ = cl.champ || "categorie";
    const champs = { [champ]: cible.cat.valeur, ...(cible.imm ? { logement: `immeuble:${cible.imm}` } : {}),
      ...(champ === "categorie" && (actuelle === COMPAGNIE || cible.cat.valeur === COMPAGNIE) ? { hors_compagnie: cible.cat.valeur === COMPAGNIE ? null : true } : {}) };
    setEnvoi(true);
    try {
      const { data } = await api.post(LOT, { ids, champs, memoriser });
      toast.success(memoriser ? `« ${benef} » déplacé dans ${nomCible(cible)} et mémorisé pour ses prochaines opérations` : `Opération déplacée dans ${nomCible(cible)}`,
        { action: { label: "Annuler", onClick: annulerDeplacement(ids, data, memoriser, cl.onClasse, onAnnule) }, duration: 10000 });
      onDeplace(ids);
      cl.onClasse();
    } catch (e) { toast.error(errMsg(e)); setEnvoi(false); }
  };
  if (envoi) return <Loader2 className="h-4 w-4 animate-spin text-slate-400" />;
  const choisirCat = (v) => setCible({ cat: cl.choix.find((x) => x.valeur === v) });
  if (cible && (!cible.cat.par_immeuble || cible.imm || !cl.immeubles.length)) {
    return <Portee benef={benef} n={memes.length} envoyer={envoyer} retour={() => setCible(null)} testId={testId} />;
  }
  if (cible) {
    return (
      <Select key={`imm-${cible.cat.valeur}`} defaultOpen onValueChange={(imm) => setCible({ ...cible, imm })} onOpenChange={(o) => { if (!o) { setTimeout(() => setCible((c) => (c?.imm ? c : null)), 0); } }}>
        <SelectTrigger className={TRIGGER} data-testid={`${testId}-immeuble`}><SelectValue placeholder={`${cible.cat.libelle.replace(" (par immeuble)", "")} · immeuble…`} /></SelectTrigger>
        <SelectContent className="max-h-72">{cl.immeubles.map((n) => <SelectItem key={n} value={n} data-testid={`${testId}-imm-${slug(n)}`}>{n}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  return (
    <Select key="cat" onValueChange={choisirCat}>
      <SelectTrigger className={TRIGGER} data-testid={testId}><SelectValue placeholder="Déplacer vers…" /></SelectTrigger>
      <SelectContent className="max-h-72">{cl.choix.filter((c) => c.libelle !== actuelle).map((c) => <SelectItem key={c.valeur} value={c.valeur} data-testid={`${testId}-option-${slug(c.valeur)}`}>{c.libelle}</SelectItem>)}</SelectContent>
    </Select>
  );
};
